import { Copy, DeviceMobile, LinkBreak } from '@phosphor-icons/react';
import type { PostShareLinkDto } from '@reelplicate/api-client/model';
import {
  useCreatePostShare,
  useRevokePostShare,
} from '@reelplicate/api-client/post-shares';
import { Button, Inline, Stack, toast, Well } from '@reelplicate/ui';
import { useEffect, useState } from 'react';

import { formatWhen } from '@/lib/format-when';

/**
 * "Post from phone": a QR code to a page with the post's caption and files,
 * for posting by hand in TikTok while publishing isn't connected. Nothing is
 * made until asked; a new link replaces the old one, and Revoke kills it.
 */
export function PostSharePanel({
  workspaceId,
  postId,
}: {
  workspaceId: string;
  postId: string;
}) {
  const create = useCreatePostShare();
  const revoke = useRevokePostShare();
  const [link, setLink] = useState<PostShareLinkDto | null>(null);
  const qr = useQrCode(link?.url);
  const local = link !== null && !isReachableFromPhone(link.url);

  const onCreate = async () => {
    try {
      setLink(await create.mutateAsync({ id: workspaceId, postId }));
    } catch {
      // Shown as a toast.
    }
  };
  const onRevoke = async () => {
    try {
      await revoke.mutateAsync({ id: workspaceId, postId });
    } catch {
      return;
    }
    setLink(null);
    toast.success('Link revoked');
  };
  const onCopy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link.url);
    toast.success('Link copied');
  };

  return (
    <section
      aria-label="Post from phone"
      className="border-border-subtle flex flex-col gap-3 rounded-lg border p-4"
    >
      <Inline gap={2} alignY="top">
        <DeviceMobile
          aria-hidden
          className="text-icon-secondary mt-0.5 size-5 shrink-0"
        />
        <Stack gap={1} className="min-w-0 flex-1">
          <h3 className="text-label-md text-text-primary">Post from phone</h3>
          <p className="text-body-sm text-text-secondary">
            Scan to save the files and copy the caption on your phone, then post it in
            TikTok.
          </p>
        </Stack>
        {!link && (
          <Button size="sm" loading={create.isPending} onClick={() => void onCreate()}>
            Create link
          </Button>
        )}
      </Inline>

      {link && (
        <Stack gap={3} align="center">
          {qr ? (
            <img
              src={qr}
              alt="QR code for the post’s phone link"
              width={200}
              height={200}
              className="rounded-md"
            />
          ) : (
            <span className="bg-bg-subtle size-[200px] rounded-md" />
          )}
          <p className="text-caption text-text-secondary text-center">
            Works until {formatWhen(link.expiresAt)} · revocable
          </p>
          {local && (
            <Well tone="warning">
              Your phone can’t open {new URL(link.url).host}. Set PUBLIC_APP_URL in the
              API’s .env to a tunnel to the web app (e.g. ngrok).
            </Well>
          )}
          <Inline gap={2}>
            <Button
              size="sm"
              variant="outline"
              leadingIcon={Copy}
              onClick={() => void onCopy()}
            >
              Copy link
            </Button>
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={LinkBreak}
              loading={revoke.isPending}
              onClick={() => void onRevoke()}
            >
              Revoke
            </Button>
          </Inline>
        </Stack>
      )}
    </section>
  );
}

/** A link a phone on another network can open: https and not this machine. */
function isReachableFromPhone(url: string) {
  const { protocol, hostname } = new URL(url);
  return protocol === 'https:' && !['localhost', '127.0.0.1', '[::1]'].includes(hostname);
}

/** The QR code as an image; black on white so phones read it in dark mode too. */
function useQrCode(url: string | undefined) {
  const [drawn, setDrawn] = useState<{ url: string; image: string } | null>(null);
  useEffect(() => {
    if (!url) return;
    let live = true;
    void import('qrcode')
      .then((qrcode) =>
        qrcode.toDataURL(url, {
          width: 400,
          margin: 1,
          color: { dark: '#000000', light: '#ffffff' },
        }),
      )
      .then((image) => {
        if (live) setDrawn({ url, image });
      });
    return () => {
      live = false;
    };
  }, [url]);
  return drawn && drawn.url === url ? drawn.image : null;
}
